import { useEffect, useRef, useState } from 'react';

/**
 * Screen Wake Lock API — mantiene la pantalla encendida mientras el
 * dispositivo de la patrulla está capturando GPS en un tramo activo de un
 * Recorregut de Vigilància (plan `2026-07-23-rv-mapa-gps-tiempo-real`,
 * Bloque D, caso 7 "segundo plano": si la pantalla se apaga, la mayoría de
 * navegadores móviles congelan/matan `watchPosition`).
 *
 * `active` = condición de negocio completa (rol Tablet operando + RV
 * in-progress + tramo activo) — la decide el caller (`RoutePreviewPage`).
 *
 * Feature-detection: `navigator.wakeLock` no existe en todos los
 * navegadores (Safari < 16.4, Firefox sin flag habilitado) — aunque el
 * `lib.dom.d.ts` de TS lo tipa como no-opcional (asume un navegador
 * moderno), en runtime puede faltar → no-op silencioso, nunca throw. Los
 * `catch` cubren además el rechazo típico de `request()` por ahorro de
 * batería o falta de permiso — el GPS sigue capturando igual sin el lock,
 * es una mejora de UX, no un requisito funcional.
 *
 * Re-adquisición: el propio sistema operativo LIBERA el wake lock en
 * cuanto la pestaña deja de estar visible (`document.visibilityState !==
 * 'visible'`) — no hay forma de mantenerlo en segundo plano. Al volver a
 * primer plano hay que volver a pedirlo explícitamente vía
 * `visibilitychange`.
 */
export function useWakeLock(active: boolean): { isLocked: boolean } {
  const [isLocked, setIsLocked] = useState(false);
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    let cancelled = false;

    const acquire = async (): Promise<void> => {
      if (!active || document.visibilityState !== 'visible') return;
      if (typeof navigator === 'undefined' || !navigator.wakeLock) return;
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (cancelled) {
          // El efecto se desmontó/desactivó mientras `request()` estaba en
          // vuelo — suelta el lock recién concedido, no lo dejamos vivo.
          sentinel.release().catch(() => undefined);
          return;
        }
        sentinelRef.current = sentinel;
        setIsLocked(true);
        sentinel.addEventListener('release', () => setIsLocked(false));
      } catch {
        // Ahorro de batería / permiso denegado / navegador sin soporte real
        // pese al feature-detect — silencioso, nunca bloquea el tracking.
      }
    };

    const release = (): void => {
      sentinelRef.current?.release().catch(() => undefined);
      sentinelRef.current = null;
      setIsLocked(false);
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') void acquire();
    };

    if (active) {
      void acquire();
      document.addEventListener('visibilitychange', onVisibilityChange);
    } else {
      release();
    }

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      release();
    };
  }, [active]);

  return { isLocked };
}

export default useWakeLock;
